import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import type { CandidateFile, CandidateFileType } from '@/types/candidates';
import { ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { FILE_TYPE_LABEL } from '../lib/file-labels';
import { CandidateFileItem } from './candidate-file-item';

interface CandidateFileGroupProps {
  candidateId: string;
  fileType: CandidateFileType;
  files: CandidateFile[];
  onPreview: (file: CandidateFile) => void;
}

export function CandidateFileGroup({
  candidateId,
  fileType,
  files,
  onPreview,
}: CandidateFileGroupProps) {
  const [open, setOpen] = useState(files.length > 0);

  // The parent keys groups by file type, so a group survives the refetch that
  // follows an upload — its mount-time default would keep the new file hidden
  // behind a collapsed header whose count badge already counts it. Re-open
  // whenever the group holds files again.
  useEffect(() => {
    if (files.length > 0) setOpen(true);
  }, [files.length]);

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="rounded-md border border-border bg-background"
    >
      <CollapsibleTrigger className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left hover:bg-muted/40">
        <ChevronRight
          className={cn(
            'h-4 w-4 text-muted-foreground transition-transform',
            open && 'rotate-90',
          )}
        />
        <div className="text-sm font-medium text-foreground">{FILE_TYPE_LABEL[fileType]}</div>
        <div className="text-xs text-muted-foreground">· {files.length}</div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="space-y-1.5 border-t border-border p-2">
          {files.length === 0 ? (
            <div className="rounded-md border border-dashed border-border bg-muted/20 px-3 py-4 text-center text-xs text-muted-foreground">
              No files in this group yet.
            </div>
          ) : (
            files.map((f) => (
              <CandidateFileItem
                key={f.id}
                candidateId={candidateId}
                file={f}
                onPreview={onPreview}
              />
            ))
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
